import React, { useState, useEffect } from "react";
import { Clock, Plus, Minus, Edit2, Check, X } from "lucide-react";
import { useTranslation } from "react-i18next";

interface TimeAdjustmentControlsProps {
  clientId: string;
  currentTime: number;
  clientName: string;
  onAdjust: (adjustment: number) => Promise<void>;
}

export const TimeAdjustmentControls: React.FC<TimeAdjustmentControlsProps> = ({
  currentTime,
  onAdjust,
}) => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [customTime, setCustomTime] = useState(currentTime);
  const [showCustomInput, setShowCustomInput] = useState(false);

  useEffect(() => {
    setCustomTime(currentTime);
  }, [currentTime]);

  const handleQuickAdjust = async (minutes: number) => {
    setIsLoading(true);
    try {
      await onAdjust(currentTime + minutes);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCustomAdjust = async () => {
    if (customTime === currentTime) {
      setShowCustomInput(false);
      return;
    }

    setIsLoading(true);
    try {
      await onAdjust(customTime);
      setShowCustomInput(false);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
      <div className="flex items-center gap-2 mb-3">
        <Clock className="w-4 h-4 text-sky-700" />
        <span className="text-sm font-medium text-gray-700">
          {t("queue.serviceDuration")}
        </span>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        {/* Bouton -5 */}
        <button
          onClick={() => handleQuickAdjust(-5)}
          disabled={isLoading || currentTime <= 5}
          className="flex items-center gap-1 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed transition font-medium shadow-sm"
        >
          <Minus className="w-4 h-4" />
          <span>5</span>
        </button>

        {/* Durée actuelle */}
        <div className="flex items-center gap-2 px-4 py-2 bg-slate-900 text-white rounded-lg font-bold shadow-sm">
          <Clock className="w-5 h-5" />
          <span className="text-lg">{currentTime} {t("common.minutes") || "min"}</span>
        </div>

        {/* Bouton +5 */}
        <button
          onClick={() => handleQuickAdjust(5)}
          disabled={isLoading}
          className="flex items-center gap-1 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 disabled:opacity-50 transition font-medium shadow-sm"
        >
          <Plus className="w-4 h-4" />
          <span>5</span>
        </button>

        {/* Bouton modifier manuellement */}
        {!showCustomInput ? (
          <button
            onClick={() => setShowCustomInput(true)}
            className="flex items-center gap-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition font-medium shadow-sm"
            title={t("common.edit") || "Modifier manuellement"}
          >
            <Edit2 className="w-4 h-4" />
            <span className="hidden sm:inline">{t("common.edit") || "Modifier"}</span>
          </button>
        ) : (
          <div className="flex items-center gap-2">
            <input
              type="number"
              min="5"
              max="300"
              value={customTime}
              onChange={(e) =>
                setCustomTime(Math.max(5, parseInt(e.target.value) || 5))
              }
              className="w-20 px-3 py-2 border border-gray-300 rounded-lg text-center font-semibold focus:ring-2 focus:ring-sky-600"
              autoFocus
              onKeyPress={(e) => e.key === "Enter" && handleCustomAdjust()}
            />
            <button
              onClick={handleCustomAdjust}
              disabled={isLoading || customTime === currentTime}
              className="p-2 bg-green-500 text-white rounded-lg hover:bg-green-600 disabled:opacity-50"
              title={t("common.confirm") || "Valider"}
            >
              <Check className="w-4 h-4" />
            </button>
            <button
              onClick={() => {
                setCustomTime(currentTime);
                setShowCustomInput(false);
              }}
              className="p-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400"
              title={t("common.cancel") || "Annuler"}
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>

      {isLoading && (
        <div className="mt-2 text-center">
          <span className="text-xs text-sky-700 animate-pulse">
            ⏳ {t("common.updating")}
          </span>
        </div>
      )}
    </div>
  );
};
